
/**
 * @fileoverview Componente atom FastAnswerIconMC para mostrar el icono correspondiente al tipo de respuesta rápida.
 * Utiliza memoización y CustomIconMC para renderizado eficiente.
 *
 * @module FastAnswerIconMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización.
 */

import React, { memo, useMemo } from "react";
import CustomIconMC from "../../components/CustomIconMC";
import { iconType } from "./utils/answerType";


/**
 * @typedef {Object} FastAnswerIconMCProps
 * @description Propiedades del componente FastAnswerIconMC.
 * @property {string} type - Tipo de respuesta rápida ('text', 'image', etc.)
 */

/**
 * Componente atom para mostrar el icono correspondiente al tipo de respuesta rápida.
 *
 * @component FastAnswerIconMC
 * @category Component - Atom
 *
 * @description
 * **Características:**
 * - Renderiza un icono según el tipo de respuesta rápida
 * - Utiliza CustomIconMC para consistencia visual
 * - Memoizado con React.memo y useMemo para performance
 * - Estilizado con TailwindCSS
 *
 * **Atomic Design:**
 * - Nivel: Atom
 * - Composición: Usa CustomIconMC (atom) y utilidades de tipo
 * - Dependencias: CustomIconMC, iconType
 *
 * @param {FastAnswerIconMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento renderizado del icono
 *
 * @example
 * <FastAnswerIconMC type="image" />
 *
 * @see {@link ../../components/CustomIconMC} Componente de icono
 * @see {@link ./utils/answerType} Utilidad para obtener el nombre del icono
 *
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización
 */
const FastAnswerIconMC = memo(({ type }) => {
	/**
	 * Memoiza el nombre del icono según el tipo de respuesta.
	 * @function iconName
	 * @memberof FastAnswerIconMC
	 * @returns {string} Nombre del icono
	 */
	const iconName = useMemo(() => iconType(type), [type]);

	return (
		<div className="tw-wrapper">
			<CustomIconMC
				name={iconName}
				size="base"
				className="tw-text-gray-400"
			/>
		</div>
	);
});

/**
 * Nombre de display para el componente FastAnswerIconMC.
 * @type {string}
 */
FastAnswerIconMC.displayName = 'FastAnswerIconMC';

export default FastAnswerIconMC;
